<template>

    <div v-if="show" class="modal-overlay">

        <div class="modal-content" @click.stop>

            <div class="modal-header">
                <h2>Recuperar Senha</h2>
                <button class="close-btn" @click="fecharModal">&times;</button>
            </div>
        
            <div class="modal-body">

                <form @submit.prevent="submitForm" novalidate>

                    <div class="form-group">
                        
                        <label for="email">E-mail:</label>
                        
                        <input
                            type="email"
                            id="email"
                            v-model="email"
                            required
                            placeholder="Digite Seu E-mail Cadastrado"
                            :disabled="loading"
                            @input="clearEmailErrors"
                        />
                        
                        <div v-if="errors.email" class="error-message">{{ errors.email }}</div>

                    </div>
                    
                    <div class="modal-footer">

                        <button
                            type="button"
                            class="btn-secondary"
                            @click="fecharModal"
                            :disabled="loading"
                            >
                            Cancelar
                        </button>

                        <button
                            type="submit"
                            class="btn-primary"
                            :disabled="loading || !email"
                        >
                            <span v-if="loading">Enviando...</span>
                            <span v-else>Enviar Nova Senha</span>

                        </button>

                    </div>

                </form>

            </div>

        </div>

    </div>

    <!-- Conteúdo snackbar -->
    <v-main>
        <v-container fluid class="pa-4">
            <router-view />  
        </v-container>
        <AppSnackbar />      
    </v-main>

</template>

<script setup>
    
    // Importa recursos do Vue
    import { ref, defineEmits, watch } from 'vue'
    
    // Importa axios para requisições HTTP
    import axios from 'axios'

    // Carrega o endereço das api's
    const API_BASE_URL = import.meta.env.VITE_API_URL;

    // Importa stores de erro
    import { useErroStore } from '@/stores/erroStore'
    
    // Importa componente de Snackbar
    import { useSnackbarStore } from '@/stores/snackbar'
    
    const snackbar = useSnackbarStore()

    // Define propriedades do componente modal
    const props = defineProps({
        show: {
            type: Boolean,
            default: false
        }
    })

    // Instancia store de erro
    const erroStore = useErroStore()

    // Define eventos emitidos pelo componente
    const emit = defineEmits(['update:show', 'closed'])

    // Define variáveis reativas do componente
    const email = ref('')
    const loading = ref(false)
    const errors = ref({})

    // Função para resetar o formulário
    const resetForm = () => {
        email.value = ''
        loading.value = false
        errors.value = {}
    }

    // Limpa erros de e-mail ao digitar
    const clearEmailErrors = () => {
        if (errors.value.email) {
            errors.value.email = ''
        }
    }

    // Função para fechar o modal
    const fecharModal = () => {
        emit('update:show', false)
        emit('closed')
        resetForm()
    }

    // Função para validar o formulário
    const validateForm = () => {

        errors.value = {}
        let isValid = true
        
        // Validação personalizada do e-mail
        if (!email.value) {

            erroStore.exibirErro('E-mail Obrigatório.')
            isValid = false

        } else {
            
            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
            
            if (!emailRegex.test(email.value)) {
            
                erroStore.exibirErro('E-mail  informado é inválido.')
                isValid = false
            }
        }       
  
        return isValid
    }

    // Função para chamar a API e enviar nova senha
    const submitForm = async () => {
    
        if (!validateForm()) return
    
        loading.value = true
        
        try {
            
            const respostaApi = await axios.post(`${API_BASE_URL}/operacao/enviaNovaSenha`, {
                emailColaborador: email.value.trim(),
            });

            if (respostaApi.data.erro == false) {

                snackbar.trigger('Nova Senha Enviada Para E-mail Informado!', 'success')

                fecharModal()
            
            } else {

                erroStore.exibirErro(respostaApi.data.mensagem) 
                loading.value = false
            }

        } catch (erro) {
            
            erroStore.exibirErro(erro)
            loading.value = false
        }
    }
    
</script>

<style scoped>
    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000;
        animation: fadeIn 0.3s ease;
    }

    .modal-content {
        background: white;
        border-radius: 8px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        width: 100%;
        max-width: 400px;
        overflow: hidden;
        animation: slideIn 0.3s ease;
    }

    .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
    }

    .modal-header h2 {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 600;
    }

    .close-btn {
        background: none;
        border: none;
        color: white;
        font-size: 28px;
        cursor: pointer;
        line-height: 1;
        padding: 0;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        transition: background-color 0.2s;
    }

    .close-btn:hover {
        background-color: rgba(255, 255, 255, 0.2);
    }

    .modal-body {
        padding: 25px;
    }

    .form-group {
        margin-bottom: 20px;
    }

    .form-group label {
        display: block;
        margin-bottom: 8px;
        font-weight: 500;
        color: #333;
    }

    .form-group input {
        width: 100%;
        padding: 12px;
        border: 1px solid #ddd;
        border-radius: 6px;
        font-size: 16px;
        transition: border-color 0.3s, box-shadow 0.3s;
    }

    .form-group input:focus {
        outline: none;
        border-color: #667eea;
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }

    .form-group input:disabled {
        background-color: #f5f5f5;
        cursor: not-allowed;
    }

    .modal-footer {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        margin-top: 25px;
    }

    .btn-primary, .btn-secondary {
        padding: 12px 24px;
        border: none;
        border-radius: 6px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.3s;
    }

    .btn-primary {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
    }

    .btn-primary:hover:not(:disabled) {
        opacity: 0.9;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
    }

    .btn-primary:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .btn-secondary {
        background-color: #f5f5f5;
        color: #666;
    }

    .btn-secondary:hover:not(:disabled) {
        background-color: #e5e5e5;
    }

    .btn-secondary:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .error-message {
        color: #e74c3c;
        font-size: 14px;
        margin-top: 5px;
    }

    .success-message {
        color: #27ae60;
        font-size: 14px;
        margin-top: 15px;
        padding: 12px;
        background-color: rgba(39, 174, 96, 0.1);
        border-radius: 6px;
        border-left: 4px solid #27ae60;
    }

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }

    @keyframes slideIn {
        from {
            opacity: 0;
            transform: translateY(-20px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
</style>